import { useState, useRef, useMemo, useEffect } from "react";
import { DateTime } from "luxon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/Dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/AlertDialog";
import { Icon } from "@/components/ui/Icon";
import { Switch } from "@/components/ui/Switch";
import {
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  EventRepeatRule,
  type Event,
} from "@/api/clubsApi";
import { buildCreateEventParams } from "./buildCreateEventParams";
import { cn } from "@/components/ui/cn";
import { LocationPickerModal, LocationView } from "@/components/location";
import { uploadImage } from "@/utils/uploadUtils";
import type { ImageUploadResult } from "@/utils/uploadUtils";
import { useToast } from "@/components/ui/Toast";
import type { GeocodingFeature } from "@tinyclub/shared/models";

// ---------------------------------------------------------------------------
// EventFormContent — standalone form body used by both Dialog and SidePanel
// ---------------------------------------------------------------------------

interface EventFormContentProps {
  clubId: string;
  creatorId: string;
  eventType: "hang" | "event";
  onClose: () => void;
  // Edit mode
  eventId?: string;
  existingEvent?: Event;
  onDeleted?: () => void;
  /** Show the icon + title header row (used in Dialog, hidden when SidePanelHeader provides the title) */
  showHeader?: boolean;
}

type StartTimeOption = "now" | "5m" | "15m" | "custom";
type DurationOption = "30m" | "1hr" | "2hr" | "custom";

/**
 * EventFormContent — the form fields, footer buttons, location picker, and delete dialog.
 * Can be embedded in a Dialog (CreateEventForm) or a SidePanel/SidePanelStack.
 */
export function EventFormContent({
  clubId,
  creatorId,
  eventType,
  onClose,
  eventId,
  existingEvent,
  onDeleted,
  showHeader = true,
}: EventFormContentProps) {
  const isHang = eventType === "hang";
  const isEditMode = Boolean(eventId);

  // Common form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationObject, setLocationObject] = useState<GeocodingFeature | null>(null);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploadResult, setImageUploadResult] = useState<ImageUploadResult | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  // Hang-specific state
  const [startTimeOption, setStartTimeOption] = useState<StartTimeOption>("now");
  const [durationOption, setDurationOption] = useState<DurationOption>("1hr");
  const [allowsQuickReplies, setAllowsQuickReplies] = useState(true);
  const [customStartTime, setCustomStartTime] = useState("");
  const [customEndTime, setCustomEndTime] = useState("");

  // Gather-specific state
  const [isAllDay, setIsAllDay] = useState(false);
  const [includeEndTime, setIncludeEndTime] = useState(false);
  const defaultDateTime = useMemo(() => {
    const dt = DateTime.now().plus({ hours: 1 }).startOf("hour");
    return { date: dt.toFormat("yyyy-MM-dd"), time: dt.toFormat("HH:mm") };
  }, []);
  const [startDate, setStartDate] = useState(defaultDateTime.date);
  const [startTime, setStartTime] = useState(defaultDateTime.time);
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isVirtual, setIsVirtual] = useState(false);
  const [virtualLinkUrl, setVirtualLinkUrl] = useState("");
  const [shareableLink, setShareableLink] = useState(true);
  const [allowDiscovery, setAllowDiscovery] = useState(false);
  const [showAttendees, setShowAttendees] = useState(true);
  const [repeatRule, setRepeatRule] = useState<EventRepeatRule | null>(null);

  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Mutations
  const [createEvent, { isLoading: isCreatingEvent }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdatingEvent }] = useUpdateEventMutation();
  const [deleteEvent, { isLoading: isDeletingEvent }] = useDeleteEventMutation();

  const isLoading = isCreatingEvent || isUpdatingEvent || isDeletingEvent;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Prepopulate form fields from existingEvent when entering edit mode
  useEffect(() => {
    if (!isEditMode || !existingEvent) return;

    setTitle(existingEvent.title || "");
    setDescription(existingEvent.description || "");
    setLocationName(existingEvent.location_name || "");
    setLocationObject((existingEvent.location_object as GeocodingFeature) || null);
    setAllowsQuickReplies(existingEvent.allows_quick_replies ?? true);
    setIsAllDay(existingEvent.is_all_day ?? false);
    setIsVirtual(existingEvent.is_virtual ?? false);
    setVirtualLinkUrl(existingEvent.virtual_link_url || "");
    setShareableLink(existingEvent.allows_public_link ?? true);
    setRepeatRule((existingEvent.repeat_rule as EventRepeatRule) || null);

    if (existingEvent.primary_image_medium || existingEvent.primary_image_small) {
      setImagePreview(existingEvent.primary_image_medium || existingEvent.primary_image_small || null);
    }

    if (isHang) {
      // For hangs in edit mode, use custom times since we can't determine the original relative offset
      setStartTimeOption("custom");
      setDurationOption("custom");
    }

    if (existingEvent.start_time) {
      const startDt = DateTime.fromISO(existingEvent.start_time, { zone: existingEvent.timezone || userTimezone });
      if (isHang) {
        setCustomStartTime(startDt.toFormat("yyyy-MM-dd'T'HH:mm"));
      } else {
        setStartDate(startDt.toFormat("yyyy-MM-dd"));
        setStartTime(startDt.toFormat("HH:mm"));
      }
    }

    if (existingEvent.end_time) {
      const endDt = DateTime.fromISO(existingEvent.end_time, { zone: existingEvent.timezone || userTimezone });
      if (isHang) {
        setCustomEndTime(endDt.toFormat("yyyy-MM-dd'T'HH:mm"));
      } else {
        setIncludeEndTime(true);
        setEndDate(endDt.toFormat("yyyy-MM-dd"));
        setEndTime(endDt.toFormat("HH:mm"));
      }
    }
  }, [isEditMode, existingEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate start and end times based on options
  const { computedStartDT, computedEndDT } = useMemo(() => {
    const now = DateTime.now();

    if (isHang) {
      // Calculate start time for hangs
      let start: DateTime;
      switch (startTimeOption) {
        case "now":
          start = now;
          break;
        case "5m":
          start = now.plus({ minutes: 5 });
          break;
        case "15m":
          start = now.plus({ minutes: 15 });
          break;
        case "custom":
          start = customStartTime
            ? DateTime.fromFormat(customStartTime, "yyyy-MM-dd'T'HH:mm", { zone: userTimezone })
            : now;
          break;
        default:
          start = now;
      }

      // Calculate end time for hangs
      let end: DateTime;
      if (durationOption === "custom" && customEndTime) {
        end = DateTime.fromFormat(customEndTime, "yyyy-MM-dd'T'HH:mm", { zone: userTimezone });
      } else {
        switch (durationOption) {
          case "30m":
            end = start.plus({ minutes: 30 });
            break;
          case "1hr":
            end = start.plus({ hours: 1 });
            break;
          case "2hr":
            end = start.plus({ hours: 2 });
            break;
          default:
            end = start.plus({ hours: 1 });
        }
      }

      return { computedStartDT: start, computedEndDT: end };
    } else {
      // Calculate start/end for gathers
      let start: DateTime;
      if (startDate && startTime && !isAllDay) {
        start = DateTime.fromFormat(`${startDate}T${startTime}`, "yyyy-MM-dd'T'HH:mm", { zone: userTimezone });
      } else if (startDate && isAllDay) {
        start = DateTime.fromFormat(startDate, "yyyy-MM-dd", { zone: userTimezone }).startOf("day");
      } else {
        start = now.plus({ hours: 1 }).startOf("hour");
      }

      let end: DateTime | null = null;
      if (includeEndTime) {
        if (endDate && endTime && !isAllDay) {
          end = DateTime.fromFormat(`${endDate}T${endTime}`, "yyyy-MM-dd'T'HH:mm", { zone: userTimezone });
        } else if (endDate && isAllDay) {
          end = DateTime.fromFormat(endDate, "yyyy-MM-dd", { zone: userTimezone }).endOf("day");
        }
      }

      return { computedStartDT: start, computedEndDT: end || start.plus({ hours: 1 }) };
    }
  }, [isHang, startTimeOption, durationOption, customStartTime, customEndTime, startDate, startTime, endDate, endTime, isAllDay, includeEndTime, userTimezone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!isHang && !title.trim()) return;
    if (isHang && !locationObject) return;

    try {
      // Build event params
      const eventParams = buildCreateEventParams({
        clubId,
        creatorId,
        title,
        description,
        isHang,
        locationName,
        locationObject,
        startTimeISO: computedStartDT.toISO(),
        endTimeISO: computedEndDT.toISO(),
        userTimezone,
        allowsQuickReplies,
        isAllDay,
        isVirtual,
        virtualLinkUrl,
        shareableLink,
        eventId,
        imageUploadResult,
        repeatRule,
      });

      if (isEditMode && eventId) {
        // Update existing event
        const { id: _id, club_id: _clubId, ...updateFields } = eventParams;
        await updateEvent({
          id: eventId,
          club_id: clubId,
          ...updateFields,
          location_object: (updateFields.location_object as GeocodingFeature) ?? null,
        }).unwrap();
      } else {
        // Create the event — the DB trigger `materialize_event_occurrences`
        // auto-creates the occurrence row from start_time/end_time, so we
        // don't need a separate createEventOccurrence call.
        const createdEvent = await createEvent(eventParams).unwrap();

        if (!createdEvent?.id) {
          throw new Error("Failed to create event");
        }
      }

      // Close on success
      onClose();
    } catch (error) {
      console.error(isEditMode ? "Failed to update event:" : "Failed to create event:", error);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImagePreview(URL.createObjectURL(file));
    setIsUploadingImage(true);
    setUploadProgress(0);

    try {
      const result = await uploadImage(file, (progress) => {
        setUploadProgress(progress);
      });
      setImageUploadResult(result);
    } catch (error) {
      console.error("Failed to upload event image:", error);
      showToast("Failed to upload image. Please try again.", "error");
      setImagePreview(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImageUploadResult(null);
  };

  // If the new start is at or after the current end, bump end to start + 1 hour
  const adjustEndAfterStartChange = (newDate: string, newTime: string) => {
    if (!endDate) return;
    const startDt = isAllDay
      ? DateTime.fromFormat(newDate, "yyyy-MM-dd")
      : DateTime.fromFormat(`${newDate}T${newTime}`, "yyyy-MM-dd'T'HH:mm");
    const endDt = isAllDay
      ? DateTime.fromFormat(endDate, "yyyy-MM-dd")
      : DateTime.fromFormat(`${endDate}T${endTime}`, "yyyy-MM-dd'T'HH:mm");
    if (startDt.isValid && endDt.isValid && startDt >= endDt) {
      const newEnd = startDt.plus({ hours: 1 });
      setEndDate(newEnd.toFormat("yyyy-MM-dd"));
      if (!isAllDay) {
        setEndTime(newEnd.toFormat("HH:mm"));
      }
    }
  };

  // Validation for submit button
  const canCreate = isHang
    ? !!locationObject && !isLoading && !isUploadingImage
    : title.trim().length > 0 && startDate && !isLoading && !isUploadingImage;

  return (
    <>
      <form onSubmit={handleSubmit}>
        {showHeader && (
          <div className="flex items-center gap-3 text-h5 mb-6">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isHang ? "bg-yellow" : "bg-accent-secondary"
              }`}
            >
              <Icon
                name={isHang ? "bolt" : "calendar-plus"}
                size={20}
                className={isHang ? "text-deep-slate" : "text-white"}
              />
            </div>
            <span>{isEditMode ? (isHang ? "Edit Hang" : "Edit Event") : (isHang ? "New Hang" : "New Event")}</span>
          </div>
        )}

        <div className="space-y-6">
          {isHang ? (
            // ========== HANG FORM ==========
            <>
              {/* Where section - Required for hangs */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">Where</span>
                  <span className="text-body-2 text-text-tertiary">Required</span>
                </div>
                <LocationView
                  editable
                  location={locationObject}
                  locationName={locationName}
                  emptyStateTitle="Where are you hanging out?"
                  onLocationClick={() => setLocationPickerOpen(true)}
                  onClearLocation={() => {
                    setLocationObject(null);
                    setLocationName("");
                  }}
                />
              </div>

              {/* When section */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">When</span>
                </div>
                <div className="rounded-2xl bg-surface p-4 space-y-4">
                  {/* Start time buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {(["now", "5m", "15m", "custom"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setStartTimeOption(option)}
                        className={cn(
                          "px-4 py-2 rounded-full text-body-2-medium transition-colors",
                          startTimeOption === option
                            ? "bg-text-primary text-background"
                            : "bg-surface-1 text-text-primary hover:bg-surface-2"
                        )}
                      >
                        {option === "now" ? "Now" : option === "5m" ? "In 5m" : option === "15m" ? "In 15m" : "Custom"}
                      </button>
                    ))}
                  </div>
                  {startTimeOption === "custom" && (
                    <input
                      type="datetime-local"
                      value={customStartTime}
                      onChange={(e) => setCustomStartTime(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-surface-1 text-text-primary text-body-2 focus:outline-none"
                    />
                  )}
                </div>
              </div>

              {/* Duration section */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">Duration</span>
                </div>
                <div className="rounded-2xl bg-surface p-4 space-y-4">
                  <div className="flex gap-2 flex-wrap">
                    {(["30m", "1hr", "2hr", "custom"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setDurationOption(option)}
                        className={cn(
                          "px-4 py-2 rounded-full text-body-2-medium transition-colors",
                          durationOption === option
                            ? "bg-text-primary text-background"
                            : "bg-surface-1 text-text-primary hover:bg-surface-2"
                        )}
                      >
                        {option === "custom" && durationOption === "custom" && customEndTime
                          ? `until ${DateTime.fromFormat(customEndTime, "yyyy-MM-dd'T'HH:mm").toFormat("h:mma").toLowerCase()}`
                          : option === "30m" ? "30m" : option === "1hr" ? "1hr" : option === "2hr" ? "2hr" : "Custom"}
                      </button>
                    ))}
                  </div>
                  {durationOption === "custom" && (
                    <input
                      type="datetime-local"
                      value={customEndTime}
                      onChange={(e) => setCustomEndTime(e.target.value)}
                      min={customStartTime || undefined}
                      className="w-full px-3 py-2 rounded-lg bg-surface-1 text-text-primary text-body-2 focus:outline-none"
                    />
                  )}
                </div>
              </div>

              {/* Quick replies toggle */}
              <div className="rounded-2xl bg-surface p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-body-2 text-text-primary">Allow quick replies</span>
                  <Switch checked={allowsQuickReplies} onCheckedChange={setAllowsQuickReplies} />
                </div>
                <p className="text-body-2 text-text-tertiary">"See you there!" | "Hope to join"</p>
              </div>

              {/* Extras section */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">Extras</span>
                </div>
                <div className="space-y-4">
                  {/* Image upload for hangs */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  {imagePreview ? (
                    <div className="relative aspect-square w-full rounded-2xl overflow-hidden border border-border-tertiary">
                      <img
                        src={imagePreview}
                        alt="Hang preview"
                        className="w-full h-full object-cover"
                      />
                      {isUploadingImage && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-white text-body-2-medium">
                            {Math.round(uploadProgress * 100)}%
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={removeImage}
                        disabled={isUploadingImage}
                        className="absolute top-3 right-3 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                      >
                        <Icon name="xmark" size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleImageClick}
                      className="w-full py-4 rounded-full border border-dashed border-border-secondary flex items-center justify-center gap-3 hover:bg-surface-1 transition-colors"
                    >
                      <Icon name="image" size={20} className="text-text-tertiary" />
                      <span className="text-body-1-medium text-text-tertiary">Add photo</span>
                    </button>
                  )}

                  {/* Optional note */}
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional note about your hang..."
                    className="w-full min-h-[60px] p-4 rounded-2xl bg-surface text-body-1 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </>
          ) : (
            // ========== GATHER FORM ==========
            <>
              {/* Header Image */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                {imagePreview ? (
                  <div className="relative aspect-square w-full rounded-2xl overflow-hidden border border-border-tertiary">
                    <img
                      src={imagePreview}
                      alt="Event preview"
                      className="w-full h-full object-cover"
                    />
                    {isUploadingImage && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-body-2-medium">
                          {Math.round(uploadProgress * 100)}%
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={removeImage}
                      disabled={isUploadingImage}
                      className="absolute top-3 right-3 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                    >
                      <Icon name="xmark" size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={handleImageClick}
                      className="absolute bottom-4 right-4 px-4 py-2 rounded-full bg-black/60 text-white text-body-2-medium hover:bg-black/70 transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleImageClick}
                    className="w-full py-4 rounded-full border border-dashed border-border-secondary flex items-center justify-center gap-3 hover:bg-surface-1 transition-colors"
                  >
                    <Icon name="image" size={20} className="text-text-tertiary" />
                    <span className="text-body-1-medium text-text-tertiary">Add photo</span>
                  </button>
                )}
              </div>

              {/* Event name */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">Event name</span>
                  <span className="text-body-2 text-text-tertiary">Required</span>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface">
                  <Icon name="globe" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Title your event"
                    className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary focus:outline-none"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">Describe the event</span>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface min-h-[100px]">
                  <Icon name="comment" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a description"
                    className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none min-h-[60px]"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Date and time settings */}
              <div className="rounded-2xl bg-surface p-4 space-y-4">
                {/* All-day toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-body-2 text-text-primary">All-day</span>
                  <Switch checked={isAllDay} onCheckedChange={setIsAllDay} />
                </div>

                <div className="h-px bg-border-tertiary" />

                {/* Include end time toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-body-2 text-text-primary">Include end time</span>
                  <Switch
                    checked={includeEndTime}
                    onCheckedChange={(checked) => {
                      setIncludeEndTime(checked);
                      if (checked && !endDate) {
                        setEndDate(startDate || defaultDateTime.date);
                        if (!isAllDay) {
                          const dt = startTime
                            ? DateTime.fromFormat(startTime, "HH:mm").plus({ hours: 1 })
                            : DateTime.now().plus({ hours: 2 }).startOf("hour");
                          setEndTime(dt.toFormat("HH:mm"));
                        }
                      }
                    }}
                  />
                </div>

                <div className="h-px bg-border-tertiary" />

                {/* Starts row */}
                <div className="flex items-center justify-between">
                  <span className="text-body-2 text-text-primary">Starts</span>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setStartDate(newDate);
                        if (includeEndTime) {
                          adjustEndAfterStartChange(newDate, startTime);
                        }
                      }}
                      className="px-3 py-2 rounded-lg bg-surface-1 text-text-primary text-body-2-medium focus:outline-none"
                    />
                    {!isAllDay && (
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => {
                          const newTime = e.target.value;
                          setStartTime(newTime);
                          if (includeEndTime) {
                            adjustEndAfterStartChange(startDate, newTime);
                          }
                        }}
                        className="px-3 py-2 rounded-lg bg-surface-1 text-text-primary text-body-2-medium focus:outline-none"
                      />
                    )}
                  </div>
                </div>

                {/* Ends row */}
                {includeEndTime && (
                  <>
                    <div className="h-px bg-border-tertiary" />
                    <div className="flex items-center justify-between">
                      <span className="text-body-2 text-text-primary">Ends</span>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate}
                          className="px-3 py-2 rounded-lg bg-surface-1 text-text-primary text-body-2-medium focus:outline-none"
                        />
                        {!isAllDay && (
                          <input
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-surface-1 text-text-primary text-body-2-medium focus:outline-none"
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Repeat rule picker */}
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <span className="text-h6 text-text-primary">Repeats</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {([null, ...Object.values(EventRepeatRule)] as const).map((option) => (
                    <button
                      key={option ?? "never"}
                      type="button"
                      onClick={() => setRepeatRule(option)}
                      className={cn(
                        "px-4 py-2 rounded-full text-body-2-medium transition-colors capitalize",
                        repeatRule === option
                          ? "bg-text-primary text-background"
                          : "bg-surface text-text-primary hover:bg-surface-1"
                      )}
                    >
                      {option ?? "Never"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Virtual event toggle */}
              <div className="rounded-2xl bg-surface p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-body-2-medium text-text-primary">Virtual event</span>
                    <p className="text-body-2 text-text-secondary mt-1">
                      Virtual events use links rather than map locations.
                    </p>
                  </div>
                  <Switch checked={isVirtual} onCheckedChange={setIsVirtual} />
                </div>
                {isVirtual && (
                  <>
                    <div className="h-px bg-border-tertiary" />
                    <div className="flex items-start gap-3">
                      <Icon name="link" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                      <input
                        type="url"
                        value={virtualLinkUrl}
                        onChange={(e) => setVirtualLinkUrl(e.target.value)}
                        placeholder="Add a meeting link"
                        className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary focus:outline-none"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Location (only if not virtual) */}
              {!isVirtual && (
                <div>
                  <div className="flex justify-between items-center mb-3 px-2">
                    <span className="text-h6 text-text-primary">Location</span>
                  </div>
                  <LocationView
                    editable
                    location={locationObject}
                    locationName={locationName}
                    emptyStateTitle="Where is this happening?"
                    onLocationClick={() => setLocationPickerOpen(true)}
                    onClearLocation={() => {
                      setLocationObject(null);
                      setLocationName("");
                    }}
                  />
                </div>
              )}

              {/* Sharing & discovery settings */}
              <div className="rounded-2xl bg-surface p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-body-2-medium text-text-primary">Shareable link</span>
                    <p className="text-body-2 text-text-secondary mt-1">
                      Allow others to share a link to this event.
                    </p>
                  </div>
                  <Switch checked={shareableLink} onCheckedChange={setShareableLink} />
                </div>

                <div className="h-px bg-border-tertiary" />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-body-2-medium text-text-primary">Allow others to discover</span>
                    <p className="text-body-2 text-text-secondary mt-1">
                      This event may be recommended to others nearby.
                    </p>
                  </div>
                  <Switch checked={allowDiscovery} onCheckedChange={setAllowDiscovery} />
                </div>

                <div className="h-px bg-border-tertiary" />

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-body-2-medium text-text-primary">Show attendees</span>
                    <p className="text-body-2 text-text-secondary mt-1">
                      Display who is going to this event.
                    </p>
                  </div>
                  <Switch checked={showAttendees} onCheckedChange={setShowAttendees} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer with buttons */}
        <div className="mt-6 flex items-center gap-3">
          {/* Delete button — edit mode only, left-aligned */}
          {isEditMode && (
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isLoading}
              className="text-body-3-medium text-red hover:opacity-80 transition-opacity"
            >
              {isHang ? "Delete hang" : "Delete event"}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            disabled={isLoading}
            onClick={onClose}
            className="px-4 py-3 rounded-full text-body-2-medium text-text-secondary hover:bg-surface-2 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canCreate}
            className={cn(
              "flex items-center justify-center gap-2 px-4 py-3 rounded-full text-body-2-medium transition-opacity",
              canCreate
                ? "bg-olive text-white hover:opacity-90"
                : "bg-olive/50 text-white/70 cursor-not-allowed"
            )}
          >
            {isLoading
              ? (isEditMode ? "Saving..." : "Creating...")
              : isEditMode
                ? "Save Changes"
                : isHang ? "Create Hang" : "Create Event"}
          </button>
        </div>
      </form>

      {/* Location Picker Modal */}
      <LocationPickerModal
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        onLocationSelect={(location, customName) => {
          setLocationObject(location);
          setLocationName(customName || "");
        }}
        initialLocation={locationObject}
        initialLocationName={locationName}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {existingEvent?.title || (isHang ? "this hang" : "this event")}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The {isHang ? "hang" : "event"} and all
              associated RSVPs will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red text-white hover:bg-red/90"
              onClick={async () => {
                if (!existingEvent?.id) return;
                try {
                  await deleteEvent({
                    eventId: existingEvent.id,
                    clubId,
                  }).unwrap();
                  setShowDeleteDialog(false);
                  onClose();
                  onDeleted?.();
                } catch (error) {
                  console.error("Failed to delete event:", error);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// CreateEventForm — Dialog wrapper for creating new events
// ---------------------------------------------------------------------------

interface CreateEventFormProps {
  clubId: string;
  creatorId: string;
  eventType: "hang" | "event";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Edit mode (legacy — prefer using EventFormContent in a SidePanel for edits)
  eventId?: string;
  existingEvent?: Event;
  onDeleted?: () => void;
}

/**
 * CreateEventForm - Dialog modal for creating new events (hangs or gathers).
 * For editing events, prefer using EventFormContent directly inside a SidePanel.
 */
export function CreateEventForm({
  clubId,
  creatorId,
  eventType,
  open,
  onOpenChange,
  eventId,
  existingEvent,
  onDeleted,
}: CreateEventFormProps) {
  const isHang = eventType === "hang";
  const isEditMode = Boolean(eventId);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        {/* Hidden DialogHeader for accessibility — actual title rendered by EventFormContent */}
        <DialogHeader className="sr-only">
          <DialogTitle>
            {isEditMode ? (isHang ? "Edit Hang" : "Edit Event") : (isHang ? "New Hang" : "New Event")}
          </DialogTitle>
        </DialogHeader>
        <EventFormContent
          clubId={clubId}
          creatorId={creatorId}
          eventType={eventType}
          eventId={eventId}
          existingEvent={existingEvent}
          onClose={handleClose}
          onDeleted={onDeleted}
          showHeader={true}
        />
      </DialogContent>
    </Dialog>
  );
}
